import { addYears, endOfMonth, format, parseISO, startOfMonth, subYears } from 'date-fns';

import { clampRangeStart } from '~/components/date/MonthRangePicker/periodRange';

export const dateToDateString = (date: Date) => {
  return format(date, 'yyyy-MM-dd');
};

/**
 * getDefaultMonthRange returns the default date range for the MonthRangePicker
 * that is the last 12 months from the current date
 * @returns { start: Date; end: Date } default date range
 * @example
 * const { start, end } = getDefaultMonthRange();
 * console.log(start, end); // [Date[2021-01-01], Date[2022-01-01]]
 */
export const getDefaultMonthRange = (): { start: Date; end: Date } => {
  const today = new Date();
  const pastYearDate = startOfMonth(subYears(today, 1));

  return { start: pastYearDate, end: endOfMonth(addYears(pastYearDate, 1)) };
};

const PERIOD_FILTER_STORAGE_KEY = 'global-period-filter';

export const saveMonthRangeToStorage = (dateRange: { start: Date; end: Date }): void => {
  try {
    localStorage.setItem(
      PERIOD_FILTER_STORAGE_KEY,
      JSON.stringify({
        start: dateToDateString(dateRange.start),
        end: dateToDateString(dateRange.end),
      }),
    );
  } catch {
    // Silently ignore storage errors
  }
};

export const getStoredOrDefaultMonthRange = (): { start: Date; end: Date } => {
  try {
    const stored = localStorage.getItem(PERIOD_FILTER_STORAGE_KEY);
    if (stored) {
      const { start, end } = JSON.parse(stored) as { start: string; end: string };
      const startDate = parseISO(start);
      const endDate = parseISO(end);
      if (!isNaN(startDate.getTime()) && !isNaN(endDate.getTime())) {
        // A range saved before the cap, or edited by hand, may be longer than the API takes.
        return { start: clampRangeStart(startDate, endDate), end: endDate };
      }
    }
  } catch {
    // Fall through to default
  }
  return getDefaultMonthRange();
};
